import { zh } from '../../utils/zh';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertCircle, Bot, CheckCircle2, Download, Eye, FileSpreadsheet, MailCheck,
    Pause, Play, RefreshCw, Send, ShieldCheck, Square, Upload, XCircle,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { authFetch } from '../../utils/authFetch';
import { SendChannelNav } from './SendChannelNav';
import { RecipientReviewPanel } from './RecipientReviewPanel';
import { EmailVerificationPanel } from './EmailVerificationPanel';
import { emailKey, LOCAL_VERIFICATION_AVAILABLE } from './emailVerification';
import { Preflight } from './sendPreparation';
import { PreflightSummary } from './PreflightSummary';
import { SendConfirmationConfig, SendConfirmationDialog } from './SendConfirmationDialog';
import { readSendSettings, saveSendSettings } from './sendSettings';
import { WorkbookSheetPicker } from './WorkbookSheetPicker';
import {
    annotateContactWorkbook, parseContactSheet, parseContactWorkbook, selectedWorkbookRows,
    EmailValidation as ValidationResult, WorkbookImport, WorkbookSource,
} from './workbookImport';

interface Campaign {
    id: string;
    name: string;
    region: string;
    targetCountries: string[];
    targetAudience?: { purpose?: string; senderEmail?: string; senderDisplayName?: string; unsubscribePresentation?: string };
    outreachMessage: { subject: string; bodyTemplate: string };
    personalizationMode?: 'template' | 'ai';
    personalizationBrief?: string;
    personalizationSignature?: string;
    leadsDiscovered?: number;
}

interface Overview {
    runtime?: 'local' | 'server';
    dispatchMode: 'development' | 'live';
    deliveryControl: {
        mode: 'test' | 'external';
        externalEnabled: boolean;
        serverLiveCapable: boolean;
        effectiveExternalEnabled: boolean;
        updatedBy?: string | null;
        updatedAt?: string | null;
    };
    worker: {
        maxLiveBatch: number;
        minDelayMs: number;
        maxDelayMs: number;
        maxPerSenderDaily: number;
        perSenderConcurrency: 1;
        senderSelection: 'least_used_round_robin';
        outlookDefaultAccounts: number;
        outlookMaxPerSenderDaily: number;
        outlookHardMaxPerSenderDaily: number;
        outlookDelayMs: number;
        outlookGlobalDelayMs?: number;
        outlookConcurrency?: number;
        outlookDelayScope?: 'sender';
    };
    totals: { configuredSenders: number; remainingCapacity: number; suppressions: number; jobs: number; activeJobs: number };
    warmupPool: { total: number; active: number; errors: number; sendingAllowed: boolean; purpose: string };
    outlookPool: {
        total: number; active: number; oauthReady: number; errors: number;
        selectedAccounts: number; accountLimit: number; maxPerAccountDaily: number;
        sentToday: number; remainingCapacity: number; delayMs: number; concurrency: number;
        senderSelection: 'least_used_round_robin';
    };
    automationApi?: {
        configured: boolean;
        authenticationHeader: string;
        allowedRegions: string[];
        mode: 'prepare_only';
        permissions: {
            validateAndImport: boolean;
            createAndEditCampaign: boolean;
            preflightAndDryRun: boolean;
            monitorAndExportFailures: boolean;
            useOutlookWarmupPool: boolean;
            approveAudience: boolean;
            liveSend: boolean;
        };
    };
    senderDomains: Array<{
        domain: string; region: string; configuredSenders: number; totalSenders: number;
        dailyLimit: number; sentToday: number; remainingCapacity: number;
    }>;
    internalTestMailboxes: Array<{
        email: string; displayName: string; domain: string; region: string;
    }>;
}

interface SendJob {
    senderAccountLimit?: number;
    excludeLawhelperSenders?: boolean;
    id: string;
    campaignId: string;
    campaignName: string;
    updatedAt?: string;
    status: string;
    dryRun: boolean;
    senderProvider: 'spacemail' | 'outlook';
    outlookPerSenderDailyLimit: number;
    totalCount: number;
    eligibleCount: number;
    processedCount: number;
    sentCount: number;
    failedCount: number;
    skippedCount: number;
    deliveryStats?: { knownInvalidRecipientCount: number; acceptedWithKnownInvalidRecipientCount: number;
        ownHardBounceRecipientCount: number; senderAccountFailureCount: number; unavailableSenderAccountCount: number };
    lastError?: string;
    createdAt: string;
    nextRunAt?: string;
}

interface SendAttempt {
    id: string;
    recipientEmail: string;
    senderEmail?: string;
    status: string;
    errorCategory?: string;
    errorCode?: string;
    errorMessage?: string;
    attemptNo: number;
    suppressed?: boolean;
    suppressionSource?: string;
    knownInvalidRecipient?: boolean;
    disposition?: 'removed_from_future_sends' | 'retry_scheduled' | 'manual_review' | null;
}

type PersonalizationStatus = 'not_generated' | 'queued' | 'generating' | 'draft' | 'approved' | 'failed';

interface PersonalizationDraft {
    leadId: string;
    name: string;
    title: string;
    companyName: string;
    email: string;
    status: PersonalizationStatus;
    subject?: string;
    body?: string;
    evidenceUsed: string[];
    model?: string;
    error?: string;
    personalizedAt?: string;
    approvedAt?: string;
}

interface PersonalizationJob {
    id: string;
    status: string;
    requestedCount: number;
    selectedCount: number;
    processedCount: number;
    draftedCount: number;
    failedCount: number;
    lastError?: string;
    createdAt: string;
}

interface PersonalizationOverview {
    configured: boolean;
    model: string;
    hardMaxJobSize: number;
    counts: Record<PersonalizationStatus, number>;
    drafts: PersonalizationDraft[];
    jobs: PersonalizationJob[];
}

interface PersonalizationApprovalPreview {
    draftCount: number;
    confirmationPhrase: string;
}

const DEFAULT_SUBJECT = 'Invitation to try a legal AI tool';
const MAX_RECIPIENTS_PER_JOB = 100_000;
const DEFAULT_BODY = `Hello {{name}},

We have developed a legal AI platform for Saudi legal practitioners. Would you be interested in trying it?

Best regards,
{{senderName}}
{{senderEmail}}`;

const DEFAULT_PERSONALIZATION_BRIEF = `Write a respectful first-contact email for a Saudi legal professional. Introduce LawHelper as a lawyer-controlled workspace for connected legal research and work product. Select only one workflow relevant to the recipient's verified role or practice area, such as Word drafting/review or spreadsheet-based analysis. Ask whether a short, no-obligation walkthrough would be useful. Do not claim a dedicated Saudi legal pack or a three-minute setup time unless those claims are independently verified before this campaign.`;

const DEFAULT_PERSONALIZATION_SIGNATURE = `Leo Yang
LawHelper`;

const COMMON_DOMAIN_TYPOS: Record<string, string> = {
    'gmai.com': 'gmail.com', 'gamil.com': 'gmail.com', 'gmal.com': 'gmail.com',
    'gmil.com': 'gmail.com', 'gmeil.com': 'gmail.com', 'gmail.con': 'gmail.com',
    'gmail.cim': 'gmail.com', 'hotmai.com': 'hotmail.com', 'hotmal.com': 'hotmail.com',
    'hotmaii.com': 'hotmail.com', 'hoymail.com': 'hotmail.com', 'hmail.com': 'hotmail.com',
    'outlook.con': 'outlook.com', 'icloud.vom': 'icloud.com', 'iclud.com': 'icloud.com',
};

const EMAIL_PATTERN = /^[^\s@<>(),;:\\"[\]]+@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

function validateEmailLocally(value: unknown): ValidationResult {
    const email = String(value ?? '').trim();
    const normalizedEmail = email.toLowerCase();
    if (!normalizedEmail) return { email, normalizedEmail, status: 'blank', reason: "邮箱单元格为空" };
    if (normalizedEmail.length > 254 || !EMAIL_PATTERN.test(normalizedEmail)) {
        return { email, normalizedEmail, status: 'invalid', reason: "邮箱格式无效" };
    }
    const at = normalizedEmail.lastIndexOf('@');
    const domain = normalizedEmail.slice(at + 1);
    const suggestedDomain = COMMON_DOMAIN_TYPOS[domain];
    if (suggestedDomain) {
        return {
            email,
            normalizedEmail,
            status: 'suspicious',
            reason: `邮箱域名可能拼写有误：${domain}`,
            suggestion: `${normalizedEmail.slice(0, at)}@${suggestedDomain}`,
        };
    }
    return { email, normalizedEmail, status: 'valid', reason: "邮箱格式检查通过" };
}

const PREVIEW_WORKBOOK_COUNTS: WorkbookImport['counts'] = {
    valid: 5487,
    blank: 4495,
    invalid: 1,
    suspicious: 17,
};

const PREVIEW_CAMPAIGN: Campaign = {
    id: 'local-preview-campaign',
    name: "沙特法律行业触达 — 本地预览",
    region: 'ae',
    targetCountries: ['SA'],
    outreachMessage: { subject: DEFAULT_SUBJECT, bodyTemplate: DEFAULT_BODY },
    personalizationMode: 'template',
    personalizationBrief: DEFAULT_PERSONALIZATION_BRIEF,
    personalizationSignature: DEFAULT_PERSONALIZATION_SIGNATURE,
};

const PREVIEW_OVERVIEW: Overview = {
    dispatchMode: 'development',
    deliveryControl: {
        mode: 'test', externalEnabled: false, serverLiveCapable: false,
        effectiveExternalEnabled: false, updatedBy: null, updatedAt: null,
    },
    worker: {
        maxLiveBatch: 100_000,
        minDelayMs: 2_500,
        maxDelayMs: 5_000,
        maxPerSenderDaily: 100,
        perSenderConcurrency: 1,
        senderSelection: 'least_used_round_robin',
        outlookDefaultAccounts: 10,
        outlookMaxPerSenderDaily: 2,
        outlookHardMaxPerSenderDaily: 100,
        outlookDelayMs: 90_000,
        outlookGlobalDelayMs: 300_000,
    },
    totals: { configuredSenders: 20, remainingCapacity: 100_000, suppressions: 0, jobs: 0, activeJobs: 0 },
    warmupPool: { total: 1010, active: 1002, errors: 8, sendingAllowed: true, purpose: 'Warmup plus reviewed outreach' },
    outlookPool: {
        total: 1010, active: 1002, oauthReady: 999, errors: 8, selectedAccounts: 10,
        accountLimit: 10, maxPerAccountDaily: 2, sentToday: 0, remainingCapacity: 20,
        delayMs: 90_000, concurrency: 1, senderSelection: 'least_used_round_robin',
    },
    automationApi: {
        configured: false,
        authenticationHeader: 'X-Automation-Key',
        allowedRegions: ['ae'],
        mode: 'prepare_only',
        permissions: {
            validateAndImport: true,
            createAndEditCampaign: true,
            preflightAndDryRun: true,
            monitorAndExportFailures: true,
            useOutlookWarmupPool: false,
            approveAudience: false,
            liveSend: false,
        },
    },
    senderDomains: [
        { domain: 'lawhelper.asia', region: 'asia', configuredSenders: 5, totalSenders: 5, dailyLimit: 250, sentToday: 0, remainingCapacity: 250 },
        { domain: 'lawhelper.eu', region: 'eu', configuredSenders: 5, totalSenders: 5, dailyLimit: 250, sentToday: 0, remainingCapacity: 250 },
        { domain: 'lawhelper.lat', region: 'lat', configuredSenders: 5, totalSenders: 5, dailyLimit: 250, sentToday: 0, remainingCapacity: 250 },
        { domain: 'lawhelper.us', region: 'us', configuredSenders: 5, totalSenders: 5, dailyLimit: 250, sentToday: 0, remainingCapacity: 250 },
    ],
    internalTestMailboxes: [
        { email: 'alex@lawhelper.asia', displayName: 'Alex | LawHelper', domain: 'lawhelper.asia', region: 'asia' },
        { email: 'sophia@lawhelper.eu', displayName: 'Sophia | LawHelper', domain: 'lawhelper.eu', region: 'eu' },
    ],
};

const PREVIEW_CUSTOM_FIELD_NAMES = ['Practice Area', 'City', 'Automation Source'];

const PREVIEW_PERSONALIZATION: PersonalizationOverview = {
    configured: true,
    model: 'gemini-3.8-flash',
    hardMaxJobSize: 1000,
    counts: { not_generated: 5467, queued: 0, generating: 0, draft: 0, approved: 20, failed: 0 },
    drafts: [
        {
            leadId: 'preview-aisha', name: 'Aisha Al-Harbi', title: 'Partner',
            companyName: 'Example Legal', email: 'aisha@example.com', status: 'approved',
            subject: 'A question about corporate work product in Riyadh',
            body: `Hi Aisha,

Given your corporate practice at Example Legal in Riyadh, I wondered whether moving between regulatory research, Word drafting, and spreadsheet-based deal analysis creates avoidable handoffs for your team.

LawHelper is a lawyer-controlled workspace that connects those materials and lets practitioners build reviewable legal workflows around their own sources. Would a short walkthrough focused on a corporate matter be useful? If not, no problem at all.

Leo Yang
LawHelper`,
            evidenceUsed: ['Name: Aisha Al-Harbi', 'Role: Partner', 'Organization: Example Legal', 'Practice area: Corporate law', 'City: Riyadh'],
            model: 'gemini-3.8-flash', personalizedAt: new Date().toISOString(), approvedAt: new Date().toISOString(),
        },
        {
            leadId: 'preview-omar', name: 'Omar Al-Qahtani', title: 'Counsel',
            companyName: 'Example Chambers', email: 'omar@example.com', status: 'approved',
            subject: 'Document review workflows for dispute work',
            body: `Hi Omar,

Your dispute-resolution role at Example Chambers raised a practical question: would it help to keep source research, document comparison, and Word redlining in one reviewable workspace rather than moving material between separate tools?

That is the workflow LawHelper is being built to support, with lawyers retaining the review and approval points. Would you be open to a brief look using a dispute-related example? A simple yes or no is helpful.

Leo Yang
LawHelper`,
            evidenceUsed: ['Name: Omar Al-Qahtani', 'Role: Counsel', 'Organization: Example Chambers', 'Practice area: Dispute resolution', 'City: Jeddah'],
            model: 'gemini-3.8-flash', personalizedAt: new Date().toISOString(), approvedAt: new Date().toISOString(),
        },
    ],
    jobs: [{
        id: 'preview-personalization-job', status: 'completed', requestedCount: 20, selectedCount: 20,
        processedCount: 20, draftedCount: 20, failedCount: 0, createdAt: new Date().toISOString(),
    }],
};

const PREVIEW_PERSONALIZATION_APPROVAL: PersonalizationApprovalPreview = {
    draftCount: 0,
    confirmationPhrase: 'APPROVE AI DRAFTS 0',
};

const PREVIEW_PREFLIGHT: Preflight = {
    deliveryMode: 'test',
    senderProvider: 'outlook',
    senderAccountLimit: 10,
    outlookPerSenderDailyLimit: 2,
    totalCount: 50,
    eligibleCount: 20,
    invalidCount: 0,
    suspiciousCount: 0,
    suppressedCount: 0,
    alreadySentCount: 0,
    notApprovedCount: 0,
    duplicateCount: 0,
    missingSenderCount: 0,
    missingCustomFieldCount: 0,
    missingPersonalizationCount: 0,
    missingConsentCount: 0,
    deferredByLimitCount: 5467,
    senderCapacity: 20,
    capacityShortfall: 0,
    confirmationPhrase: 'SEND OUTLOOK 20 MAX 2',
    issues: [],
    byDomain: [{ domain: "Outlook 授权账号池", recipients: 20, remainingCapacity: 20, configuredSenders: 10 }],
};

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await authFetch(path, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) throw new Error(data.error || `请求失败（${response.status}）`);
    return data as T;
}

function csvCell(value: unknown): string {
    const raw = String(value ?? '');
    const safe = /^\s*[=+\-@]/.test(raw) ? `'${raw}` : raw;
    return `"${safe.replace(/"/g, '""')}"`;
}

function statusTone(status: string): string {
    if (['completed', 'sent', 'dry_run', 'approved'].includes(status)) return 'success';
    if (['failed', 'cancelled', 'uncertain'].includes(status)) return 'danger';
    if (['paused', 'draft'].includes(status)) return 'warning';
    return 'info';
}

function renderPreviewTemplate(
    template: string,
    vars: { name: string; company: string; country: string; senderName: string; senderEmail: string; customFields: Record<string, string> }
): string {
    const standard: Record<string, string> = {
        name: vars.name, company: vars.company, country: vars.country,
        senderName: vars.senderName, senderEmail: vars.senderEmail,
    };
    return template.replace(/\{\{\s*([^{}\r\n]+?)\s*\}\}/g, (original, raw: string) => {
        const field = raw.trim();
        if (Object.prototype.hasOwnProperty.call(standard, field)) return standard[field];
        if (field.startsWith('custom.')) {
            const key = field.slice('custom.'.length).trim();
            return vars.customFields[key] || `〔missing: ${key}〕`;
        }
        return original;
    });
}

export function BulkSendPage({ previewMode = false }: { previewMode?: boolean }) {
    const savedSettingsRef = useRef(previewMode ? {} : readSendSettings());
    const initializedCampaignSelectionRef = useRef(false);
    const [overview, setOverview] = useState<Overview | null>(() => previewMode ? PREVIEW_OVERVIEW : null);
    const [campaigns, setCampaigns] = useState<Campaign[]>(() => previewMode ? [PREVIEW_CAMPAIGN] : []);
    const [jobs, setJobs] = useState<SendJob[]>([]);
    const [selectedCampaignId, setSelectedCampaignId] = useState(
        () => new URLSearchParams(window.location.search).get('campaign')
            || (previewMode ? PREVIEW_CAMPAIGN.id : savedSettingsRef.current.campaignId || '')
    );
    const [selectedJobId, setSelectedJobId] = useState('');
    const [attempts, setAttempts] = useState<SendAttempt[]>([]);
    const [workbookSource, setWorkbookSource] = useState<WorkbookSource | null>(null);
    const [workbookImport, setWorkbookImport] = useState<WorkbookImport | null>(null);
    const [verifiedImportEmails, setVerifiedImportEmails] = useState<Set<string> | null>(null);
    const [verifyingEmails, setVerifyingEmails] = useState(false);
    const [preflight, setPreflight] = useState<Preflight | null>(() => previewMode ? PREVIEW_PREFLIGHT : null);
    const [checkingPreflight, setCheckingPreflight] = useState(false);
    const [preflightError, setPreflightError] = useState('');
    const [preflightRevision, setPreflightRevision] = useState(0);
    const [sendConfirmation, setSendConfirmation] = useState<SendConfirmationConfig | null>(null);
    const [exclusionsOpen, setExclusionsOpen] = useState(() => window.location.hash === '#recipient-review');
    const [personalizationOverview, setPersonalizationOverview] = useState<PersonalizationOverview | null>(
        () => previewMode ? PREVIEW_PERSONALIZATION : null
    );
    const [personalizationApproval, setPersonalizationApproval] = useState<PersonalizationApprovalPreview | null>(
        () => previewMode ? PREVIEW_PERSONALIZATION_APPROVAL : null
    );
    const [campaignName, setCampaignName] = useState(`Saudi legal outreach ${new Date().toISOString().slice(0, 10)}`);
    const [sourceDescription, setSourceDescription] = useState('Saudi legal market workbook; professional contact details reviewed for relevant B2B outreach.');
    const [subject, setSubject] = useState(DEFAULT_SUBJECT);
    const [bodyTemplate, setBodyTemplate] = useState(DEFAULT_BODY);
    const [draftMode, setDraftMode] = useState<'template' | 'ai'>('template');
    const [personalizationBrief, setPersonalizationBrief] = useState(DEFAULT_PERSONALIZATION_BRIEF);
    const [personalizationSignature, setPersonalizationSignature] = useState(DEFAULT_PERSONALIZATION_SIGNATURE);
    const [personalizationCount, setPersonalizationCount] = useState(20);
    const [personalizationDataAcknowledged, setPersonalizationDataAcknowledged] = useState(previewMode);
    const [senderProvider, setSenderProvider] = useState<'spacemail' | 'outlook'>(() => savedSettingsRef.current.senderProvider || 'outlook');
    const [senderAccountLimit, setSenderAccountLimit] = useState(() => savedSettingsRef.current.senderAccountLimit || 10);
    const [outlookPerSenderDailyLimit, setOutlookPerSenderDailyLimit] = useState(() => savedSettingsRef.current.outlookPerSenderDailyLimit || 2);
    const [batchLimit, setBatchLimit] = useState(() => savedSettingsRef.current.batchLimit || 20);
    const [fieldInsertTarget, setFieldInsertTarget] = useState<'subject' | 'body'>('body');
    const [testSenderEmail, setTestSenderEmail] = useState('');
    const [testRecipientEmail, setTestRecipientEmail] = useState('');
    const [testName] = useState('LawHelper team');
    const [testCompany] = useState('LawHelper');
    const [testCountry] = useState('Saudi Arabia');
    const [testFields] = useState<Array<{ id: string; key: string; value: string }>>([
        { id: crypto.randomUUID(), key: 'Practice Area', value: 'Corporate law' },
        { id: crypto.randomUUID(), key: 'City', value: 'Riyadh' },
        { id: crypto.randomUUID(), key: 'Automation Source', value: 'Internal workflow test' },
    ]);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const idempotencyRef = useRef(crypto.randomUUID());
    const liveIntentRef = useRef<{ signature: string; key: string } | null>(null);
    const loadedMessageCampaignRef = useRef('');
    const initializedOutlookDailyLimitRef = useRef(previewMode || savedSettingsRef.current.outlookPerSenderDailyLimit !== undefined);
    const workbookRequestRef = useRef(0);

    const selectedCampaign = useMemo(
        () => campaigns.find((campaign) => campaign.id === selectedCampaignId) || null,
        [campaigns, selectedCampaignId]
    );
    const externalMode = Boolean(overview?.deliveryControl.effectiveExternalEnabled);
    const externalSwitchOn = Boolean(overview?.deliveryControl.externalEnabled);
    const validBatchLimit = Number.isInteger(batchLimit) && batchLimit >= 1 && batchLimit <= MAX_RECIPIENTS_PER_JOB;
    const selectedCampaignIsSaudi = selectedCampaign?.targetCountries.some((country) => country.toUpperCase() === 'SA') ?? true;

    const internalMailboxes = useMemo(() => overview?.internalTestMailboxes || [], [overview]);
    const detectedCustomFields = workbookImport?.customFieldNames
        || (previewMode ? PREVIEW_CUSTOM_FIELD_NAMES : []);
    const testCustomFields = useMemo(() => Object.fromEntries(
        testFields.filter((field) => field.key.trim()).map((field) => [field.key.trim(), field.value])
    ), [testFields]);
    const previewSender = internalMailboxes.find((mailbox) => mailbox.email === testSenderEmail) || internalMailboxes[0];
    const firstImportedRow = workbookImport?.rows.find((row) => row.email.trim()) || null;
    const previewVariables = {
        name: firstImportedRow?.name || testName || 'there',
        company: firstImportedRow?.companyName || testCompany,
        country: firstImportedRow?.country || testCountry,
        senderName: previewSender?.displayName || 'LawHelper sender',
        senderEmail: previewSender?.email || 'sender@lawhelper.*',
        customFields: firstImportedRow?.customFields || testCustomFields,
    };
    const renderedSubjectPreview = renderPreviewTemplate(subject, previewVariables);
    const templateBodyPreview = renderPreviewTemplate(bodyTemplate, previewVariables).trim();
    const renderedBodyPreview = externalMode && templateBodyPreview && !/\bunsubscribe\b/i.test(templateBodyPreview)
        ? `${templateBodyPreview}\n\nIf you prefer not to receive further messages, reply "Unsubscribe".`
        : templateBodyPreview;

    const loadDashboard = useCallback(async (quiet = false) => {
        if (previewMode) {
            setOverview(PREVIEW_OVERVIEW);
            setCampaigns([PREVIEW_CAMPAIGN]);
            setJobs([]);
            setSelectedCampaignId(PREVIEW_CAMPAIGN.id);
            if (!quiet) setNotice("本地预览已刷新。");
            return;
        }
        if (!quiet) setBusy('dashboard');
        try {
            const [overviewData, campaignData, jobData] = await Promise.all([
                api<{ overview: Overview }>('/bulk-send/overview'),
                api<{ campaigns: Campaign[] }>('/campaigns'),
                api<{ jobs: SendJob[] }>('/send-jobs?limit=30'),
            ]);
            setOverview(overviewData.overview);
            setCampaigns(campaignData.campaigns || []);
            setJobs(jobData.jobs || []);
            // Only choose a default on first load. An empty selection afterward
            // means the operator chose to create a new campaign.
            if (!initializedCampaignSelectionRef.current) {
                initializedCampaignSelectionRef.current = true;
                setSelectedCampaignId((current) => campaignData.campaigns?.some((campaign) => campaign.id === current)
                    ? current
                    : savedSettingsRef.current.campaignId === '' ? '' : campaignData.campaigns?.[0]?.id || '');
            }
        } catch (err) {
            if (!quiet) setError(err instanceof Error ? err.message : String(err));
        } finally {
            if (!quiet) setBusy('');
        }
    }, [previewMode]);

    const loadPersonalization = useCallback(async (quiet = false, campaignId = selectedCampaignId) => {
        if (previewMode) {
            setPersonalizationOverview(PREVIEW_PERSONALIZATION);
            setPersonalizationApproval(PREVIEW_PERSONALIZATION_APPROVAL);
            return;
        }
        if (!campaignId) {
            setPersonalizationOverview(null);
            return;
        }
        if (!quiet) setBusy('personalization-load');
        try {
            const data = await api<{ overview: PersonalizationOverview }>(`/campaigns/${campaignId}/personalizations?limit=20`);
            setPersonalizationOverview(data.overview);
        } catch (err) {
            if (!quiet) setError(err instanceof Error ? err.message : String(err));
        } finally {
            if (!quiet) setBusy('');
        }
    }, [previewMode, selectedCampaignId]);

    useEffect(() => { void loadDashboard(); }, [loadDashboard]);
    useEffect(() => { void loadPersonalization(true); }, [loadPersonalization]);
    useEffect(() => {
        if (!validBatchLimit) {
            setPreflight(null); setCheckingPreflight(false); return;
        }
        if (previewMode || !externalMode || !selectedCampaignId) return;
        let cancelled = false;
        setCheckingPreflight(true); setPreflightError('');
        const timer = setTimeout(() => {
            void api<{ preflight: Preflight }>(`/campaigns/${selectedCampaignId}/send-preflight`, {
                method: 'POST', body: JSON.stringify({ limit: batchLimit, senderProvider,
                    senderAccountLimit: senderProvider === 'outlook' ? senderAccountLimit : undefined,
                    outlookPerSenderDailyLimit: senderProvider === 'outlook' ? outlookPerSenderDailyLimit : undefined }),
            }).then((data) => { if (!cancelled) setPreflight(data.preflight); })
                .catch((err) => { if (!cancelled) { setPreflight(null); setPreflightError(err instanceof Error ? err.message : String(err)); } })
                .finally(() => { if (!cancelled) setCheckingPreflight(false); });
        }, 600);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [previewMode, externalMode, selectedCampaignId, selectedCampaign?.leadsDiscovered,
        senderProvider, senderAccountLimit, outlookPerSenderDailyLimit, batchLimit,
        overview?.totals.suppressions, preflightRevision, validBatchLimit]);
    useEffect(() => {
        if (!overview || initializedOutlookDailyLimitRef.current) return;
        setOutlookPerSenderDailyLimit(overview.worker.outlookMaxPerSenderDaily || 2);
        initializedOutlookDailyLimitRef.current = true;
    }, [overview]);
    useEffect(() => {
        if (previewMode) return;
        if (initializedCampaignSelectionRef.current) {
            saveSendSettings({ campaignId: selectedCampaignId, senderProvider, senderAccountLimit,
                outlookPerSenderDailyLimit, batchLimit });
        }
        const timer = setInterval(() => {
            void loadDashboard(true);
            void loadPersonalization(true);
        }, 8_000);
        return () => clearInterval(timer);
    }, [loadDashboard, loadPersonalization, previewMode, selectedCampaignId,
        senderProvider, senderAccountLimit, outlookPerSenderDailyLimit, batchLimit]);

    useEffect(() => {
        if (previewMode || !selectedJobId) return;
        let active = true;
        let loading = false;
        const timer = setInterval(async () => {
            if (loading) return;
            loading = true;
            try {
                const data = await api<{ attempts: SendAttempt[] }>(`/send-jobs/${selectedJobId}/attempts?limit=1000`);
                if (active) setAttempts(data.attempts || []);
            } catch { /* The dashboard refresh reports connection failures. */ }
            finally { loading = false; }
        }, 8_000);
        return () => { active = false; clearInterval(timer); };
    }, [previewMode, selectedJobId]);

    useEffect(() => {
        if (!internalMailboxes.length) return;
        if (!internalMailboxes.some((mailbox) => mailbox.email === testSenderEmail)) {
            setTestSenderEmail(internalMailboxes[0].email);
        }
        if (!internalMailboxes.some((mailbox) => mailbox.email === testRecipientEmail)) {
            setTestRecipientEmail(internalMailboxes[0].email);
        }
    }, [internalMailboxes, testRecipientEmail, testSenderEmail]);

    useEffect(() => {
        if (!selectedCampaign || loadedMessageCampaignRef.current === selectedCampaign.id) return;
        loadedMessageCampaignRef.current = selectedCampaign.id;
        setSubject(selectedCampaign.outreachMessage?.subject || DEFAULT_SUBJECT);
        setBodyTemplate(selectedCampaign.outreachMessage?.bodyTemplate || DEFAULT_BODY);
        setDraftMode(selectedCampaign.personalizationMode === 'ai' ? 'ai' : 'template');
        setPersonalizationBrief(selectedCampaign.personalizationBrief || DEFAULT_PERSONALIZATION_BRIEF);
        setPersonalizationSignature(selectedCampaign.personalizationSignature || DEFAULT_PERSONALIZATION_SIGNATURE);
        if (selectedCampaign.targetAudience?.purpose === 'AI-personalized follow-up to a previously successful initial invitation') {
            setSenderProvider('spacemail');
            setBatchLimit(20);
        }
        if (previewMode) {
            setPreflight(PREVIEW_PREFLIGHT);
            return;
        }
        setPreflight(null);
        setPersonalizationApproval(null);
        setPersonalizationDataAcknowledged(previewMode);
    }, [previewMode, selectedCampaign]);

    useEffect(() => {
        if (previewMode) return;
        setPreflight(null);
    }, [previewMode, senderProvider, senderAccountLimit, outlookPerSenderDailyLimit, batchLimit]);

    async function checkWorkbook(source: WorkbookSource, request = ++workbookRequestRef.current) {
        setWorkbookSource(source);
        setWorkbookImport(null);
        setVerifiedImportEmails(null); setVerifyingEmails(false);
        setBusy('workbook'); setError(''); setNotice('');
        try {
            const rows = selectedWorkbookRows(source.sheets);
            if (!rows.length) {
                setNotice('尚未选择可导入的工作表。请在下方勾选工作表，或为未识别的表指定表头和邮箱列。');
                return;
            }
            const validations: ValidationResult[] = [];
            // Bound each validation request, including large multi-sheet files.
            for (let offset = 0; offset < rows.length; offset += 5_000) {
                const chunk = rows.slice(offset, offset + 5_000);
                const results = previewMode
                    ? chunk.map((row) => validateEmailLocally(row.email))
                    : (await api<{ results: ValidationResult[] }>('/bulk-send/validate', {
                        method: 'POST', body: JSON.stringify({ emails: chunk.map((row) => row.email) }),
                    })).results;
                if (request !== workbookRequestRef.current) return;
                if (results.length !== chunk.length) throw new Error('邮箱检查结果不完整，请点击“重新检查”。');
                validations.push(...results);
            }
            const counts: WorkbookImport['counts'] = { valid: 0, blank: 0, invalid: 0, suspicious: 0 };
            validations.forEach((result) => { counts[result.status] += 1; });
            const selectedSheets = source.sheets.filter((sheet) => sheet.selected && !sheet.issue);
            setWorkbookImport({
                ...source, rows, validations, counts,
                customFieldNames: Array.from(new Set(selectedSheets.flatMap((sheet) => sheet.customFieldNames))),
            });
            setNotice(`已检查 ${selectedSheets.length} 张工作表，共 ${rows.length.toLocaleString()} 行数据。${previewMode ? '文件仅保留在浏览器中。' : '尚未导入联系人或发送邮件。'}`);
        } catch (err) {
            if (request === workbookRequestRef.current) setError(err instanceof Error ? err.message : String(err));
        } finally {
            if (request === workbookRequestRef.current) setBusy('');
        }
    }

    async function handleWorkbook(file?: File) {
        if (!file) return;
        const request = ++workbookRequestRef.current;
        setWorkbookSource(null); setWorkbookImport(null);
        setBusy('workbook'); setError(''); setNotice('');
        try {
            const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellStyles: true });
            if (request !== workbookRequestRef.current) return;
            await checkWorkbook(parseContactWorkbook(workbook, file.name), request);
        } catch (err) {
            if (request === workbookRequestRef.current) setError(err instanceof Error ? err.message : String(err));
        } finally {
            if (request === workbookRequestRef.current) setBusy('');
        }
    }

    function selectWorkbookSheet(name: string, selected: boolean) {
        if (!workbookSource) return;
        void checkWorkbook({ ...workbookSource, sheets: workbookSource.sheets.map((sheet) =>
            sheet.name === name ? { ...sheet, selected } : sheet
        ) });
    }

    function mapWorkbookSheet(name: string, headerRowIndex: number, emailColumn: number) {
        if (!workbookSource) return;
        const mapped = parseContactSheet(workbookSource.workbook, name, { headerRowIndex, emailColumn });
        mapped.selected = !mapped.issue;
        void checkWorkbook({ ...workbookSource, sheets: workbookSource.sheets.map((sheet) =>
            sheet.name === name ? mapped : sheet
        ) });
    }

    function exportAnnotatedWorkbook() {
        if (!workbookImport) return;
        try {
            const { workbook, sheets, rows, validations, fileName } = workbookImport;
            const annotated = annotateContactWorkbook(workbook, sheets, rows, validations);
            const stem = fileName.replace(/\.xlsx?$/i, '');
            XLSX.writeFile(annotated, `${stem}_邮箱检查结果.xlsx`, { cellStyles: true });
        } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    }

    function insertTemplateToken(token: string) {
        if (fieldInsertTarget === 'subject') {
            setSubject((current) => `${current}${current && !current.endsWith(' ') ? ' ' : ''}${token}`);
        } else {
            setBodyTemplate((current) => `${current}${current && !current.endsWith('\n') ? '\n' : ''}${token}`);
        }
    }

    async function sendInternalTest() {
        if (previewMode) return;
        setBusy('internal-test'); setError(''); setNotice('');
        try {
            const customFields: Record<string, string> = {};
            for (const field of testFields) {
                const key = field.key.trim();
                if (!key) continue;
                if (Object.prototype.hasOwnProperty.call(customFields, key)) throw new Error(`测试变量名称重复：${key}`);
                customFields[key] = field.value;
            }
            const data = await api<{
                result: { messageId?: string; senderEmail: string; recipientEmail: string; dispatchMode: 'development' | 'live' };
            }>('/bulk-send/internal-test', {
                method: 'POST',
                body: JSON.stringify({
                    senderEmail: testSenderEmail,
                    recipientEmail: testRecipientEmail,
                    subject,
                    bodyTemplate,
                    name: testName,
                    company: testCompany,
                    country: testCountry,
                    customFields,
                }),
            });
            setNotice(data.result.dispatchMode === 'live'
                ? `内部测试已从 ${data.result.senderEmail} 发送至 ${data.result.recipientEmail}。邮件编号：${data.result.messageId || '—'}`
                : "内部测试邮件已在开发模式下生成，未实际发出。");
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy('');
        }
    }

    async function ensureCampaign(): Promise<string> {
        if (selectedCampaignId) return selectedCampaignId;
        if (!campaignName.trim()) throw new Error("请输入活动名称。");
        const created = await api<{ campaign: Campaign }>('/campaigns', {
            method: 'POST',
            body: JSON.stringify({
                name: campaignName.trim(), region: 'ae', targetCountries: ['SA'],
                targetAudience: {
                    industry: 'Legal services', targetRole: 'Legal professional',
                    qualifiers: sourceDescription, exclusions: '',
                },
                outreachMessage: { subject, bodyTemplate },
                personalizationMode: draftMode,
            }),
        });
        setSelectedCampaignId(created.campaign.id);
        return created.campaign.id;
    }

    async function saveMessage(
        campaignId = selectedCampaignId,
        personalizationMode: 'template' | 'ai' = draftMode
    ) {
        if (!campaignId) throw new Error("请先选择一个活动。");
        await api(`/campaigns/${campaignId}/message`, {
            method: 'PATCH', body: JSON.stringify({ subject, bodyTemplate, personalizationMode }),
        });
    }

    async function changeDraftMode(nextMode: 'template' | 'ai') {
        if (nextMode === draftMode) return;
        const previousMode = draftMode;
        setDraftMode(nextMode);
        setPreflight(null);
        setError('');
        setNotice('');

        if (previewMode || !selectedCampaignId) return;

        setBusy('draft-mode');
        try {
            await saveMessage(selectedCampaignId, nextMode);
            setCampaigns((current) => current.map((campaign) => campaign.id === selectedCampaignId
                ? { ...campaign, personalizationMode: nextMode, outreachMessage: { subject, bodyTemplate } }
                : campaign));
            setNotice(nextMode === 'template'
                ? "模板模式已保存。发送时会自动填入联系人变量，无需 Gemini。"
                : "AI 个性化模式已保存。对外发送前，需要审核通过每位收件人的草稿。");
        } catch (err) {
            setDraftMode(previousMode);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy('');
        }
    }

    async function saveTemplate() {
        if (previewMode) return;
        setBusy('template-save'); setError(''); setNotice('');
        try {
            const campaignId = await ensureCampaign();
            await saveMessage(campaignId, 'template');
            setDraftMode('template');
            setCampaigns((current) => current.map((campaign) => campaign.id === campaignId
                ? { ...campaign, personalizationMode: 'template', outreachMessage: { subject, bodyTemplate } }
                : campaign));
            setPreflight(null);
                setNotice("模板已保存。任务运行时会自动填入变量，无需生成 AI 草稿。");
            await loadDashboard(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy('');
        }
    }

    async function generatePersonalizedDrafts() {
        if (previewMode) return;
        setBusy('personalize'); setError(''); setNotice('');
        try {
            const campaignId = await ensureCampaign();
            const data = await api<{ job: PersonalizationJob; reused: boolean }>(`/campaigns/${campaignId}/personalizations/jobs`, {
                method: 'POST',
                body: JSON.stringify({
                    count: personalizationCount,
                    brief: personalizationBrief,
                    signature: personalizationSignature,
                    idempotencyKey: crypto.randomUUID(),
                    dataProcessingAcknowledged: personalizationDataAcknowledged,
                }),
            });
            setPreflight(null);
            setPersonalizationApproval(null);
            setNotice(externalMode
                ? `已加入 ${data.job.selectedCount.toLocaleString()} 份个性化草稿生成任务，正式发送前需完成审核。`
                : `已加入 ${data.job.selectedCount.toLocaleString()} 份测试草稿生成任务，测试模式无需收件人及发送审批。`);
            await Promise.all([loadDashboard(true), loadPersonalization(true, campaignId)]);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy('');
        }
    }

    function editPersonalizedDraft(leadId: string, field: 'subject' | 'body', value: string) {
        setPersonalizationOverview((current) => current ? {
            ...current,
            drafts: current.drafts.map((draft) => draft.leadId === leadId ? { ...draft, [field]: value } : draft),
        } : current);
    }

    async function savePersonalizedDraft(draft: PersonalizationDraft, approve = false) {
        if (previewMode) return;
        setBusy(`personalization-${draft.leadId}`); setError(''); setNotice('');
        try {
            await api(`/leads/${draft.leadId}/personalization`, {
                method: 'PATCH',
                body: JSON.stringify({ subject: draft.subject || '', body: draft.body || '', approve }),
            });
            setNotice(approve ? `已审核 ${draft.name || draft.email} 的个性化草稿。` : `已保存 ${draft.name || draft.email} 的个性化草稿。`);
            setPreflight(null);
            await loadPersonalization(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy('');
        }
    }

    async function loadPersonalizationApproval() {
        if (!selectedCampaignId || previewMode) return;
        setBusy('personalization-approval'); setError('');
        try {
            const data = await api<{ preview: PersonalizationApprovalPreview }>(
                `/campaigns/${selectedCampaignId}/personalizations/approval-preview`
            );
            setPersonalizationApproval(data.preview);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy('');
        }
    }

    async function approvePersonalizations() {
        if (!selectedCampaignId || !personalizationApproval || previewMode) return;
        setBusy('personalization-approve'); setError(''); setNotice('');
        try {
            const data = await api<{ approved: number }>(`/campaigns/${selectedCampaignId}/personalizations/approve-bulk`, {
                method: 'POST',
                body: JSON.stringify({ confirmationPhrase: personalizationApproval?.confirmationPhrase }),
            });
            setNotice(`已审核 ${data.approved.toLocaleString()} 份个性化草稿。`);
            setPersonalizationApproval(null);
            setPreflight(null);
            await loadPersonalization(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy('');
        }
    }

    async function importWorkbook() {
        if (!workbookImport || verifyingEmails) return;
        const importRows = workbookImport.rows.filter(row => verifiedImportEmails === null || verifiedImportEmails.has(emailKey(row.email)));
        if (!importRows.length) { setError('检查后的保留名单为空，请先核对邮箱结果。'); return; }
        setBusy('import'); setError(''); setNotice('');
        try {
            const campaignId = await ensureCampaign();
            await saveMessage(campaignId);
            const data = await api<{ result: Record<string, number> }>('/bulk-send/import', {
                method: 'POST',
                body: JSON.stringify({ campaignId, rows: importRows, sourceDescription }),
            });
            const result = data.result;
            setNotice(`已导入 ${result.imported.toLocaleString()} 位联系人；有效性筛选跳过 ${workbookImport.rows.length - importRows.length} 行；空白 ${result.blank} 条，无效 ${result.invalid} 条，需检查 ${result.suspicious} 条，文件内重复 ${result.duplicateInFile || 0} 条，已存在 ${result.existing} 条，已排除 ${result.suppressed || 0} 条。`);
            await loadDashboard(true);
            setPreflightRevision((value) => value + 1);
        } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
        finally { setBusy(''); }
    }

    async function changeDeliveryMode(externalEnabled: boolean) {
        if (previewMode) return;
        setBusy('delivery-mode'); setError(''); setNotice('');
        try {
            const data = await api<{
                deliveryControl: Overview['deliveryControl'];
                pausedJobCount: number;
            }>('/bulk-send/delivery-control', {
                method: 'PATCH',
                body: JSON.stringify({ externalEnabled }),
            });
            setOverview((current) => current ? { ...current, deliveryControl: data.deliveryControl } : current);
            setPreflight(null);
                idempotencyRef.current = crypto.randomUUID();
            setNotice(externalEnabled
                ? "对外发送已启用。请在准备完成后创建正式发送任务。"
                : `已启用测试模式，此流程不会发出邮件。${data.pausedJobCount ? `已暂停 ${data.pausedJobCount} 个正式发送任务。` : ''}`);
            await loadDashboard(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy('');
        }
    }

    function prepareSend(audienceOnly = false) {
        if (previewMode || !externalMode || !selectedCampaign || !validBatchLimit) return;
        if (!audienceOnly && overview && batchLimit > overview.worker.maxLiveBatch) {
            setError(`当前${overview.runtime === 'local' ? '本地' : '远程'}发送服务单次上限为 ${overview.worker.maxLiveBatch.toLocaleString()} 位。${overview.runtime === 'local' ? '请检查本地服务的任务上限配置。' : '现有名单和邮箱账号尚未切换到本地，需先完成迁移。'}`);
            return;
        }
        const intent = {
            campaignId: selectedCampaign.id, campaignName: selectedCampaign.name,
            subject, bodyTemplate, draftMode, sourceDescription, requiresConsent: selectedCampaignIsSaudi,
            limit: batchLimit, senderProvider,
            senderAccountLimit: senderProvider === 'outlook' ? senderAccountLimit : undefined,
            outlookPerSenderDailyLimit: senderProvider === 'outlook' ? outlookPerSenderDailyLimit : undefined,
            audienceOnly,
            simpleSend: overview?.runtime === 'local',
        };
        const signature = JSON.stringify(intent);
        if (liveIntentRef.current?.signature !== signature) liveIntentRef.current = { signature, key: crypto.randomUUID() };
        setSendConfirmation({ ...intent, idempotencyKey: liveIntentRef.current.key });
    }

    async function createJob() {
        // Live delivery is available only through the explicit confirmation dialog.
        if (previewMode || !selectedCampaignId || externalMode || !validBatchLimit) return;
        setBusy('job'); setError(''); setNotice('');
        try {
            await saveMessage();
            const data = await api<{ job: SendJob; reused: boolean; preflight?: Preflight }>(`/campaigns/${selectedCampaignId}/send-jobs`, {
                method: 'POST', body: JSON.stringify({
                    limit: batchLimit, dryRun: true, idempotencyKey: idempotencyRef.current,
                    senderProvider,
                    senderAccountLimit: senderProvider === 'outlook' ? senderAccountLimit : undefined,
                    outlookPerSenderDailyLimit: senderProvider === 'outlook' ? outlookPerSenderDailyLimit : undefined,
                }),
            });
            if (data.preflight) setPreflight(data.preflight);
            setNotice(data.reused ? '已打开现有测试任务。' : '模拟测试已完成，未发送邮件。');
            setSelectedJobId(data.job.id);
            idempotencyRef.current = crypto.randomUUID();
            await loadDashboard(true);
            await loadAttempts(data.job.id);
        } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
        finally { setBusy(''); }
    }

    async function jobAction(jobId: string, action: 'pause' | 'resume' | 'cancel') {
        setBusy(`${action}-${jobId}`); setError('');
        try {
            await api(`/send-jobs/${jobId}/${action}`, { method: 'POST', body: '{}' });
            await loadDashboard(true);
            if (selectedJobId === jobId) await loadAttempts(jobId);
        } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
        finally { setBusy(''); }
    }

    async function loadAttempts(jobId: string) {
        setSelectedJobId(jobId);
        try {
            const data = await api<{ attempts: SendAttempt[] }>(`/send-jobs/${jobId}/attempts?limit=1000`);
            setAttempts(data.attempts || []);
        } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    }

    function downloadFailures() {
        const failed = attempts.filter((attempt) => ['failed', 'uncertain'].includes(attempt.status));
        const rows = [
            ["收件邮箱", "发件邮箱", "状态", "错误类型", "错误代码", "尝试次数", "后续处理", "说明"],
            ...failed.map((attempt) => [attempt.recipientEmail, attempt.senderEmail, attempt.status, attempt.errorCategory, attempt.errorCode, attempt.attemptNo, attempt.disposition || 'manual_review', attempt.errorMessage]),
        ];
        const blob = new Blob([rows.map((row) => row.map(csvCell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = `send_failures_${selectedJobId || 'job'}.csv`; link.click();
        URL.revokeObjectURL(url);
    }

    async function downloadPersonalizationReview() {
        if (!selectedCampaignId || previewMode) return;
        setBusy('personalization-export'); setError('');
        try {
            const data = await api<{ drafts: PersonalizationDraft[] }>(`/campaigns/${selectedCampaignId}/personalizations/export`);
            const rows = [
                ["联系人", "职位", "机构", "邮箱", "状态", "个性化主题", "个性化正文", "参考依据", "模型", "错误"],
                ...data.drafts.map((draft) => [
                    draft.name, draft.title, draft.companyName, draft.email, draft.status,
                    draft.subject, draft.body, draft.evidenceUsed.join(' | '), draft.model, draft.error,
                ]),
            ];
            const blob = new Blob([rows.map((row) => row.map(csvCell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `personalized_draft_review_${selectedCampaignId}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy('');
        }
    }

    const failedAttempts = attempts.filter((attempt) => ['failed', 'uncertain'].includes(attempt.status));
    const permanentlyRemovedAttempts = attempts.filter((attempt) => attempt.disposition === 'removed_from_future_sends');
    const retryScheduledAttempts = attempts.filter((attempt) => attempt.disposition === 'retry_scheduled');
    const manualReviewAttempts = attempts.filter((attempt) => attempt.disposition === 'manual_review');
    const activeJob = jobs.find((job) => job.id === selectedJobId);
    const qualityCounts = workbookImport?.counts || (previewMode && !workbookSource ? PREVIEW_WORKBOOK_COUNTS : null);
    const outlookGlobalDelayMs = overview?.worker.outlookGlobalDelayMs || 0;
    const outlookGlobalDelayLabel = outlookGlobalDelayMs % 60_000 === 0
        ? `${outlookGlobalDelayMs / 60_000} 分钟`
        : `${Math.ceil(outlookGlobalDelayMs / 1000)} 秒`;
    const secondsRange = overview
        ? senderProvider === 'outlook'
            ? outlookGlobalDelayMs > 0
                ? `全局至少 ${outlookGlobalDelayLabel}`
                : `${Math.round(overview.worker.outlookDelayMs / 1000)} 秒`
            : `${Math.round(overview.worker.minDelayMs / 1000)}–${Math.round(overview.worker.maxDelayMs / 1000)} 秒`
        : '—';
    const plannedOutlookAccounts = Math.min(senderAccountLimit, overview?.outlookPool.oauthReady || 0);
    const plannedOutlookCapacity = plannedOutlookAccounts * outlookPerSenderDailyLimit;
    const outlookDailyLimitAdjustable = Boolean(overview?.worker.outlookHardMaxPerSenderDaily);

    return (
        <div className="bulk-send-page">
            <SendChannelNav />
            {previewMode && (
                <div className="bulk-preview-banner">
                    <ShieldCheck size={18} />
                    <div><strong>本地示例预览</strong><span>可以选择真实 Excel 做本地解析和标记；文件不会上传，也不会写入数据库或发送邮件。</span></div>
                </div>
            )}
            <div className="bulk-header">
                <div>
                    <div className="bulk-eyebrow"><MailCheck size={14} /> 邮件触达管理</div>
                    <h2 className="page-title">批量发送中心</h2>
                    <p className="page-subtitle">选择名单 → 编辑邮件 → 准备发送。硬退信和重复邮箱会自动跳过。</p>
                    {!previewMode && overview && <p className="bulk-help">发送服务：{overview.runtime === 'local' ? '本机' : '远程'} · 单个任务人数上限 {overview.worker.maxLiveBatch.toLocaleString()}</p>}
                </div>
                <button className="btn btn-outline" onClick={() => void loadDashboard()} disabled={!!busy}>
                    <RefreshCw size={14} className={busy === 'dashboard' ? 'spin' : ''} /> 刷新
                </button>
            </div>

            <div className={`delivery-mode-control ${externalMode ? 'external' : 'test'}`}>
                <div className="delivery-mode-copy">
                    {externalMode ? <Send size={19} /> : <ShieldCheck size={19} />}
                    <div>
                        <strong>{externalMode ? "对外发送" : "测试模式"}</strong>
                        <span>{externalMode
                            ? "可创建正式发送任务；开启此开关本身不会发送邮件。"
                            : "当前不会实际发送邮件，可导入联系人、编辑草稿和模拟运行。"}</span>
                    </div>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={externalSwitchOn}
                    className={`delivery-master-switch ${externalSwitchOn ? 'on' : ''}`}
                    disabled={previewMode || busy === 'delivery-mode' || (!externalSwitchOn && overview?.deliveryControl.serverLiveCapable === false)}
                    onClick={() => void changeDeliveryMode(!externalSwitchOn)}
                >
                    <span className="delivery-switch-track"><i /></span>
                    <span>{busy === 'delivery-mode' ? "切换中…" : externalSwitchOn ? "关闭对外发送" : "启用对外发送"}</span>
                </button>
            </div>

            {error && <div className="bulk-alert danger"><XCircle size={16} /><span>{error}</span><button onClick={() => setError('')}>×</button></div>}
            {notice && <div className="bulk-alert success"><CheckCircle2 size={16} /><span>{notice}</span><button onClick={() => setNotice('')}>×</button></div>}

            <div className="bulk-layout">
                <div className="bulk-main-column">
                    <section className="card bulk-section bulk-list-section">
                        <div className="bulk-section-head">
                            <div><span className="bulk-step">1</span><div><h3>联系人列表与活动</h3><p>导入联系人表格，或选择已有获客活动。</p></div></div>
                            {workbookImport && <button className="btn btn-outline btn-sm" onClick={exportAnnotatedWorkbook}><Download size={13} /> 导出检查结果</button>}
                            {LOCAL_VERIFICATION_AVAILABLE && !workbookImport && <a className="btn btn-outline btn-sm" href="/email-validation" target="_blank" rel="noreferrer">检查客户邮箱</a>}
                        </div>

                        <div className="bulk-two-col">
                            <label className="bulk-field">
                                <span>选择已有活动</span>
                                <select className="input" value={selectedCampaignId} onChange={(event) => {
                                    initializedCampaignSelectionRef.current = true;
                                    setSelectedCampaignId(event.target.value);
                                }}>
                                    <option value="">新建沙特获客活动</option>
                                    {campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name} · {zh(campaign.region)}</option>)}
                                </select>
                            </label>
                            {!selectedCampaignId && <label className="bulk-field"><span>新活动名称</span><input className="input" value={campaignName} onChange={(event) => setCampaignName(event.target.value)} /></label>}
                        </div>

                        <label className="bulk-upload">
                            <input type="file" accept=".xlsx,.xls" disabled={!!busy} onChange={(event) => {
                                const file = event.target.files?.[0];
                                event.target.value = '';
                                void handleWorkbook(file);
                            }} />
                            <span className="bulk-upload-icon"><Upload size={20} /></span>
                            <span><strong>{workbookSource ? workbookSource.fileName : previewMode ? "选择 Excel 文件，预览导入结果" : "选择联系人 Excel 表格"}</strong><small>{busy === 'workbook' ? '正在识别工作表并检查邮箱…' : workbookSource ? `已读取全部 ${workbookSource.sheets.length} 张工作表 · 可在下方选择` : "XLSX 或 XLS · 支持多工作表 · 检查文件不会发送邮件"}</small></span>
                        </label>

                        {workbookSource && <WorkbookSheetPicker
                            source={workbookSource} disabled={!!busy}
                            onSelect={selectWorkbookSheet} onMapping={mapWorkbookSheet}
                            onRecheck={() => void checkWorkbook(workbookSource)}
                        />}

                        {qualityCounts && (
                            <div className="quality-grid">
                                <Quality label="格式有效" value={qualityCounts.valid} tone="good" />
                                <Quality label="空白" value={qualityCounts.blank} tone="muted" />
                                <Quality label="格式无效" value={qualityCounts.invalid} tone="bad" />
                                <Quality label="疑似拼写错误" value={qualityCounts.suspicious} tone="warn" />
                            </div>
                        )}

                        {detectedCustomFields.length > 0 && (
                            <div className="bulk-detected-fields">
                                <div><strong>{detectedCustomFields.length} 个自定义变量已识别</strong><span>Excel 中的其他列会作为每位联系人的自定义变量保存，可插入邮件模板。</span></div>
                                <div>{detectedCustomFields.slice(0, 12).map((field) => <code key={field}>{field}</code>)}{detectedCustomFields.length > 12 && <code>+{detectedCustomFields.length - 12} 项</code>}</div>
                            </div>
                        )}

                        {workbookImport && (
                            <details className="bulk-import-sample" open>
                                <summary>联系人解析预览 · 前 {Math.min(workbookImport.rows.length, 5)} 行</summary>
                                <div><table className="data-table"><thead><tr><th>工作表／行号</th><th>姓名</th><th>公司</th><th>邮箱</th><th>检查结果</th></tr></thead><tbody>
                                    {workbookImport.rows.slice(0, 5).map((row, index) => <tr key={`${row.sourceSheet}-${row.sourceRow}-${row.email}`}><td>{row.sourceSheet} · {row.sourceRow}</td><td>{row.name || '—'}</td><td>{row.companyName || '—'}</td><td>{row.email || '（空白）'}</td><td><span className={`job-status ${workbookImport.validations[index]?.status === 'valid' ? 'success' : workbookImport.validations[index]?.status === 'suspicious' ? 'warning' : 'danger'}`}>{zh(workbookImport.validations[index]?.status)}</span></td></tr>)}
                                </tbody></table></div>
                            </details>
                        )}

                        {LOCAL_VERIFICATION_AVAILABLE && workbookImport && <EmailVerificationPanel
                            key={workbookRequestRef.current} rows={workbookImport.rows} source={workbookImport}
                            onSelectionChange={setVerifiedImportEmails} onBusyChange={setVerifyingEmails} />}

                        <label className="bulk-field"><span>名单来源与联系理由</span><textarea className="input" rows={2} value={sourceDescription} onChange={(event) => setSourceDescription(event.target.value)} /></label>
                        <div className="bulk-actions-row">
                            <span className="bulk-help">空白、格式无效、疑似拼写错误、已排除或已存在的邮箱会跳过；文件内重复邮箱以首条资料为准。{previewMode ? " 登录后，服务器会再次检查每一行。" : ''}</span>
                            <button className="btn btn-primary" disabled={!workbookImport?.counts.valid || !!busy || verifyingEmails || verifiedImportEmails?.size === 0} onClick={() => previewMode ? window.location.assign('/bulk-send') : void importWorkbook()}>
                                <FileSpreadsheet size={14} /> {previewMode ? "登录后继续导入" : busy === 'import' ? "正在导入…" : "导入通过检查的联系人"}
                            </button>
                        </div>
                    </section>

                    <details className="bulk-exclusions-disclosure" open={exclusionsOpen} onToggle={(event) => setExclusionsOpen(event.currentTarget.open)}>
                        <summary><span>硬退信与不发送名单</span><span>已排除 {(overview?.totals.suppressions || 0).toLocaleString()} 个邮箱 · 点击管理</span></summary>
                        {exclusionsOpen && (
                    <RecipientReviewPanel
                        campaignId={selectedCampaignId}
                        campaignName={selectedCampaign?.name}
                        previewMode={previewMode}
                        refreshKey={`${overview?.totals.suppressions || 0}|${campaigns.map((campaign) => `${campaign.id}:${campaign.leadsDiscovered || 0}`).join('|')}|${jobs.map((job) => `${job.id}:${job.updatedAt || job.status}:${job.processedCount}:${job.failedCount}`).join('|')}`}
                        onExclusionsChange={() => {
                            setPreflight(null);
                            setPreflightRevision((value) => value + 1);
                            void loadDashboard(true);
                            if (selectedJobId) void loadAttempts(selectedJobId);
                        }}
                    />
                        )}
                    </details>

                    <section className="card bulk-section bulk-ai-section">
                        <div className="bulk-section-head">
                            <div><span className="bulk-step">2</span><div><h3>编辑邮件</h3><p>使用统一模板，或为每位联系人生成不同的 AI 草稿。</p></div></div>
                            {draftMode === 'ai' && <div className="draft-header-actions"><button className="btn btn-outline btn-sm" disabled={previewMode || !selectedCampaignId || !personalizationOverview?.drafts.length || busy === 'personalization-export'} onClick={() => void downloadPersonalizationReview()}><Download size={13} /> 导出草稿审核表</button><button className="btn btn-outline btn-sm" disabled={!selectedCampaignId || busy === 'personalization-load'} onClick={() => void loadPersonalization()}><RefreshCw size={13} /> 刷新草稿</button></div>}
                        </div>

                        <div className="draft-mode-selector" role="tablist" aria-label="邮件撰写方式">
                            <button type="button" role="tab" aria-selected={draftMode === 'template'} className={draftMode === 'template' ? 'active' : ''} disabled={busy === 'draft-mode'} onClick={() => void changeDraftMode('template')}>
                                <MailCheck size={16} /><span><strong>模板邮件</strong><small>统一模板，自动填入变量 · 无需 AI 密钥</small></span>
                            </button>
                            <button type="button" role="tab" aria-selected={draftMode === 'ai'} className={draftMode === 'ai' ? 'active' : ''} disabled={busy === 'draft-mode'} onClick={() => void changeDraftMode('ai')}>
                                <Bot size={16} /><span><strong>AI 个性化邮件</strong><small>可选 · Gemini 为每位联系人生成草稿</small></span>
                            </button>
                        </div>

                        {draftMode === 'ai' && <>
                        <div className={`personalization-readiness ${previewMode ? 'preview' : personalizationOverview?.configured ? 'ready' : 'blocked'}`}>
                            <Bot size={18} />
                            <div>
                                <strong>{previewMode
                                    ? "AI 草稿示例预览"
                                    : personalizationOverview?.configured
                                        ? `${personalizationOverview.model} ready`
                                        : "尚未配置 Gemini，暂时无法生成草稿。"}</strong>
                                <span>{previewMode
                                    ? "下方展示的是示例草稿。"
                                    : externalMode
                                        ? "草稿以已有联系人资料为依据；缺乏依据的表述、链接、未填变量和未审核草稿会被拦截。"
                                        : "草稿以已有联系人资料为依据，生成后仍可编辑。测试模式不会发送邮件。"}</span>
                            </div>
                        </div>

                        {externalMode && overview?.runtime !== 'local' && <div className="bulk-actions-row"><span className="bulk-help">首次生成草稿前，先确认本活动的联系人名单。</span><button className="btn btn-outline btn-sm" disabled={previewMode || !selectedCampaignId || !!busy} onClick={() => prepareSend(true)}>确认名单</button></div>}
                        <label className="bulk-field"><span>活动要求</span><textarea className="input personalization-brief" rows={5} value={personalizationBrief} onChange={(event) => setPersonalizationBrief(event.target.value)} /></label>
                        <div className="bulk-two-col">
                            <label className="bulk-field"><span>真实发件人签名</span><textarea className="input personalization-signature" rows={3} value={personalizationSignature} onChange={(event) => setPersonalizationSignature(event.target.value)} /></label>
                            <label className="bulk-field"><span>本次生成草稿数</span><input className="input" type="number" min={1} max={personalizationOverview?.hardMaxJobSize || 1000} value={personalizationCount} onChange={(event) => setPersonalizationCount(Math.min(personalizationOverview?.hardMaxJobSize || 1000, Math.max(1, Number(event.target.value) || 1)))} /><small>分批生成，便于审核；后续任务会选择尚无已审核草稿的联系人。</small></label>
                        </div>
                        <div className="bulk-actions-row">
                            <span className="bulk-help">{externalMode
                                ? "对外发送前请完成收件人审核。没有独立依据时，不得承诺“沙特专用法律包”或“三分钟完成配置”。"
                                : "测试模式可在导入后立即生成草稿，不会实际发送邮件。"}</span>
                            <button className="btn btn-primary" disabled={previewMode || !selectedCampaignId || !personalizationOverview?.configured || !personalizationDataAcknowledged || busy === 'personalize'} onClick={() => void generatePersonalizedDrafts()}>
                                {busy === 'personalize' ? <RefreshCw size={14} className="spin" /> : <Bot size={14} />} {previewMode ? "仅供预览" : busy === 'personalize' ? "正在排队…" : `生成 ${personalizationCount} 份个性化草稿`}
                            </button>
                        </div>
                        <label className="bulk-check personalization-data-check"><input type="checkbox" checked={personalizationDataAcknowledged} disabled={previewMode} onChange={(event) => setPersonalizationDataAcknowledged(event.target.checked)} /><span>我知悉，Google Gemini 将处理联系人的姓名及所选职业资料（职位、机构、业务领域、城市、简介和来源链接），用于生成草稿。提示词不包含收件人的邮箱地址。</span></label>

                        {personalizationOverview && (
                            <>
                                <div className="personalization-counts">
                                    {(['not_generated', 'queued', 'generating', 'draft', 'approved', 'failed'] as PersonalizationStatus[]).map((status) => <div key={status}><span>{zh(status)}</span><strong>{personalizationOverview.counts[status].toLocaleString()}</strong></div>)}
                                </div>
                                {personalizationOverview.jobs[0] && <div className="personalization-job"><span><b>{zh(personalizationOverview.jobs[0].status)}</b> · 最近一次生成任务</span><span>{personalizationOverview.jobs[0].processedCount}/{personalizationOverview.jobs[0].selectedCount} 已处理 · {personalizationOverview.jobs[0].draftedCount} 份草稿 · {personalizationOverview.jobs[0].failedCount} 条失败</span></div>}

                                <div className="personalization-draft-list">
                                    {personalizationOverview.drafts.length === 0 && <div className="empty-state"><span className="empty-icon">✍️</span><span>暂无生成的草稿。</span></div>}
                                    {personalizationOverview.drafts.map((draft) => (
                                        <article className="personalization-draft" key={draft.leadId}>
                                            <header><div><strong>{draft.name || draft.email}</strong><span>{[draft.title, draft.companyName, draft.email].filter(Boolean).join(' · ')}</span></div><b className={`job-status ${statusTone(draft.status)}`}>{zh(draft.status)}</b></header>
                                            {draft.error ? <div className="draft-error"><AlertCircle size={14} /> {draft.error}</div> : <>
                                                <label className="bulk-field"><span>个性化主题</span><input className="input" dir="auto" value={draft.subject || ''} maxLength={120} onChange={(event) => editPersonalizedDraft(draft.leadId, 'subject', event.target.value)} /></label>
                                                <label className="bulk-field"><span>个性化正文</span><textarea className="input draft-body" dir="auto" rows={8} value={draft.body || ''} onChange={(event) => editPersonalizedDraft(draft.leadId, 'body', event.target.value)} /></label>
                                                <div className="draft-evidence"><span>参考依据</span>{draft.evidenceUsed.map((fact) => <code key={fact}>{fact}</code>)}</div>
                                                <div className="draft-actions"><small>{externalMode ? (selectedCampaign?.targetAudience?.unsubscribePresentation === 'link' ? "发送时仅补充邮件偏好链接。" : "对外发送时仅补充回复退订说明。") : "仅保存供测试审核，未发送邮件。"}</small><span><button className="btn btn-outline btn-sm" disabled={previewMode || busy === `personalization-${draft.leadId}`} onClick={() => void savePersonalizedDraft(draft)}>保存草稿</button>{externalMode && <button className="btn btn-success btn-sm" disabled={previewMode || busy === `personalization-${draft.leadId}`} onClick={() => void savePersonalizedDraft(draft, true)}>保存并审核通过</button>}</span></div>
                                            </>}
                                        </article>
                                    ))}
                                </div>

                                {externalMode && <div className="personalization-approval">
                                    <div><strong>人工审核草稿</strong><span>请先检查上方生成的内容，再审核通过本批草稿。</span></div>
                                    <button className="btn btn-outline" disabled={previewMode || !selectedCampaignId || personalizationOverview.counts.draft === 0} onClick={() => void loadPersonalizationApproval()}>检查待审核草稿</button>
                                </div>}
                                {externalMode && personalizationApproval && personalizationApproval.draftCount > 0 && <div className="bulk-confirm-row"><span>请确认已核对这 {personalizationApproval.draftCount.toLocaleString()} 份草稿。</span><button className="btn btn-success" disabled={previewMode || busy === 'personalization-approve'} onClick={() => void approvePersonalizations()}>审核通过 {personalizationApproval.draftCount.toLocaleString()} 份草稿</button></div>}
                            </>
                        )}
                        </>}

                        {draftMode === 'template' && <div className="template-compose">
                                <div className="personalization-readiness ready">
                                    <MailCheck size={18} />
                                    <div><strong>模板模式已就绪</strong><span>无需 Gemini 密钥或单独生成草稿。准备每封邮件时，会自动填入对应联系人的变量。</span></div>
                                </div>
                                <label className="bulk-field"><span>邮件主题</span><input className="input" value={subject} maxLength={200} onChange={(event) => setSubject(event.target.value)} /></label>
                                <label className="bulk-field"><span>邮件正文</span><textarea className="input bulk-message" value={bodyTemplate} onChange={(event) => setBodyTemplate(event.target.value)} /></label>
                                <div className="bulk-help">发送前，会将模板中的 <code>{'{{name}}'}</code> 及 <code>{'{{company}}'}</code> 等变量替换为该联系人的对应资料。</div>
                                <div className="bulk-token-builder">
                                    <div className="bulk-token-head"><div><strong>插入模板变量</strong><span>点击变量，可将其添加到所选主题或正文的末尾。</span></div><div className="bulk-target-toggle"><button className={fieldInsertTarget === 'subject' ? 'active' : ''} onClick={() => setFieldInsertTarget('subject')}>邮件主题</button><button className={fieldInsertTarget === 'body' ? 'active' : ''} onClick={() => setFieldInsertTarget('body')}>邮件正文</button></div></div>
                                    <div className="bulk-token-list">{['name', 'company', 'country', 'senderName', 'senderEmail'].map((field) => <button key={field} onClick={() => insertTemplateToken(`{{${field}}}`)}>{`{{${field}}}`}</button>)}{detectedCustomFields.map((field) => <button className="custom" key={field} onClick={() => insertTemplateToken(`{{custom.${field}}}`)}>{`{{custom.${field}}}`}</button>)}</div>
                                </div>
                                <div className="bulk-message-preview"><div className="bulk-preview-head"><span><Eye size={14} /> 邮件模板预览</span><small>{firstImportedRow ? firstImportedRow.email : "内部示例资料"} · 未发送邮件</small></div><strong>{renderedSubjectPreview}</strong><pre>{renderedBodyPreview}</pre></div>
                                <div className="bulk-actions-row">
                                    <span className="bulk-help">保存模板不会发送邮件。对外发送预览已包含回复退订说明。</span>
                                    <span className="template-actions">
                                        <button className="btn btn-outline" disabled={previewMode || !subject.trim() || !bodyTemplate.trim() || busy === 'template-save'} onClick={() => void saveTemplate()}><MailCheck size={14} /> {busy === 'template-save' ? "正在保存…" : "保存模板"}</button>
                                        <button className="btn btn-success" disabled={previewMode || !externalMode || !testSenderEmail || !testRecipientEmail || busy === 'internal-test'} onClick={() => void sendInternalTest()}><Send size={14} /> {previewMode ? "登录后可用" : !externalMode ? "需启用对外发送" : busy === 'internal-test' ? "正在发送测试邮件…" : "发送 1 封内部测试邮件"}</button>
                                    </span>
                                </div>
                        </div>}
                    </section>

                    <section className="card bulk-section bulk-jobs-section">
                        <div className="bulk-section-head"><div><div><h3>发送记录</h3><p>查看任务进度、暂停或取消任务，并查看每个邮箱的失败原因。</p></div></div>{failedAttempts.length > 0 && <button className="btn btn-outline btn-sm" onClick={downloadFailures}><Download size={13} /> 导出失败记录</button>}</div>
                        <p className="bulk-help">已提交不代表送达。无效收件地址按邮箱去重，包含后续从其他发件邮箱确认的硬退信；发件账号异常单独统计。</p>
                        <div className="job-list">
                            {jobs.length === 0 && <p className="bulk-help">发送后将在这里显示进度和退信记录。</p>}
                            {jobs.map((job) => {
                                const progress = job.eligibleCount ? Math.round((job.processedCount / job.eligibleCount) * 100) : 0;
                                return <button key={job.id} className={`job-row ${selectedJobId === job.id ? 'selected' : ''}`} onClick={() => void loadAttempts(job.id)}>
                                    <span className={`job-status ${statusTone(job.status)}`}>{zh(job.status)}</span>
                                    <span className="job-copy"><strong>{job.campaignName || "获客活动"}</strong><small>{job.dryRun ? "模拟测试 · 不发邮件" : job.senderProvider === 'outlook' ? `轮换发送 · 每个账号每日最多 ${job.outlookPerSenderDailyLimit || overview?.worker.outlookMaxPerSenderDaily || 2} 封` : "对外发送"}{job.excludeLawhelperSenders ? ` · 仅非 lawhelper Outlook · 最多 ${job.senderAccountLimit} 个账号` : ""} · {new Date(job.createdAt).toLocaleString()}</small>
                                        {job.lastError && <small className="job-error">{zh(job.lastError)}{job.status === 'queued' && job.nextRunAt ? ` · 下次重试 ${new Date(job.nextRunAt).toLocaleTimeString()}` : ''}</small>}
                                    </span>
                                    <span className="job-progress"><span><i style={{ width: `${progress}%` }} /></span><small>{job.processedCount}/{job.eligibleCount} · {job.sentCount} 封{job.dryRun ? '模拟通过' : '已提交'} · {job.failedCount} 条提交失败</small>
                                        {!job.dryRun && job.deliveryStats && <small>{job.deliveryStats.knownInvalidRecipientCount} 个收件地址已确认无效 · {job.deliveryStats.unavailableSenderAccountCount} 个发件账号异常</small>}
                                    </span>
                                    <span className="job-controls" onClick={(event) => event.stopPropagation()}>
                                        {['queued', 'running'].includes(job.status) && <button title="暂停" onClick={() => void jobAction(job.id, 'pause')}><Pause size={13} /></button>}
                                        {job.status === 'paused' && <button title="继续" onClick={() => void jobAction(job.id, 'resume')}><Play size={13} /></button>}
                                        {!['completed', 'cancelled'].includes(job.status) && <button title="取消" onClick={() => void jobAction(job.id, 'cancel')}><Square size={12} /></button>}
                                    </span>
                                </button>;
                            })}
                        </div>
                        {selectedJobId && (
                            <div className="attempt-table-wrap">
                                <div className="attempt-title"><span>{activeJob?.campaignName || "所选任务"} — 发送详情</span><span>显示 {attempts.length} 条{activeJob ? ` / 共 ${activeJob.eligibleCount} 条` : ''} · 异常与已处理记录优先</span></div>
                                <div className="failure-disposition-summary"><span className="removed"><strong>{permanentlyRemovedAttempts.length}</strong> 个邮箱已永久排除</span><span className="retry"><strong>{retryScheduledAttempts.length}</strong> 个邮箱待重试</span><span className="review"><strong>{manualReviewAttempts.length}</strong> 个邮箱待人工审核</span></div>
                                <table className="data-table"><thead><tr><th>收件邮箱</th><th>发件邮箱</th><th>状态</th><th>错误类型</th><th>后续处理</th><th>原因</th></tr></thead><tbody>
                                    {attempts.length === 0 ? <tr><td colSpan={6} className="bulk-empty-cell">暂无发送记录。</td></tr> : attempts.map((attempt) => <tr key={attempt.id}><td>{attempt.recipientEmail}</td><td>{attempt.senderEmail || '—'}</td><td><span className={`job-status ${statusTone(attempt.knownInvalidRecipient ? 'failed' : attempt.status)}`}>{attempt.knownInvalidRecipient ? '收件地址无效' : attempt.status === 'sent' && !activeJob?.dryRun ? '已提交（送达待确认）' : zh(attempt.status)}</span></td><td>{zh(attempt.errorCategory)}</td><td><span className={`attempt-disposition ${attempt.disposition || ''}`}>{attempt.disposition === 'removed_from_future_sends' ? "已排除" : attempt.disposition === 'retry_scheduled' ? "待重试" : attempt.disposition === 'manual_review' ? "待审核" : '—'}</span></td><td className="attempt-reason">{attempt.knownInvalidRecipient && !attempt.errorMessage ? '已确认硬退信，详见无效收件地址名单。' : zh(attempt.errorMessage)}</td></tr>)}
                                </tbody></table>
                            </div>
                        )}
                    </section>
                </div>

                <aside className="bulk-side-column">
                    <section className="card launch-card">
                        <div className="bulk-section-head"><div><span className="bulk-step">3</span><div><h3>{externalMode ? '准备发送' : '模拟测试'}</h3><p>{externalMode ? '确认名单和数量后，再开始发送。' : '试跑流程，不发送邮件。'}</p></div></div></div>
                        <div className="launch-summary"><SummaryLine label="所选名单" value={selectedCampaign?.name || '尚未选择'} /></div>
                        <label className="bulk-field"><span>本次最多发送人数</span><input className="input" type="number" min={1} max={MAX_RECIPIENTS_PER_JOB} step={1} value={Number.isNaN(batchLimit) ? '' : batchLimit}
                            onChange={(event) => setBatchLimit(event.target.value === '' ? NaN : Number(event.target.value))}
                            onBlur={() => setBatchLimit(Math.min(MAX_RECIPIENTS_PER_JOB, Math.max(1, Math.floor(batchLimit) || 1)))}
                        /><small>单次最多 {MAX_RECIPIENTS_PER_JOB.toLocaleString()} 位，按发件设置依次发送。</small></label>
                        {senderProvider === 'outlook' && overview?.worker.outlookConcurrency && <p className="bulk-help" aria-label="并行发送设置">
                            {outlookGlobalDelayMs > 0
                                ? <>全局每封至少间隔 {outlookGlobalDelayLabel}，所有 Outlook 账号和任务共用；同时最多 {Math.min(overview.worker.outlookConcurrency, plannedOutlookAccounts)} 个账号发送，每个账号每日最多 {outlookPerSenderDailyLimit} 封。</>
                                : <>同时最多 {Math.min(overview.worker.outlookConcurrency, plannedOutlookAccounts)} 个账号发送；同一账号逐封处理，间隔 {Math.round(overview.worker.outlookDelayMs / 1000)} 秒，每日最多 {outlookPerSenderDailyLimit} 封。</>}
                        </p>}
                        <details className="send-advanced-settings">
                            <summary>发件设置 <span>{senderProvider === 'outlook' ? `Outlook · ${plannedOutlookAccounts} 个账号` : 'SpaceMail'}</span></summary>
                            <label className="bulk-field"><span>发件账号池</span><select className="input" value={senderProvider} onChange={(event) => setSenderProvider(event.target.value as 'spacemail' | 'outlook')}><option value="outlook">Outlook 授权账号池</option><option value="spacemail">SpaceMail 域名账号池</option></select></label>
                            {senderProvider === 'outlook' && <>
                                <label className="bulk-field"><span>轮换使用的账号数</span><input className="input" type="number" min={1} max={overview?.outlookPool.total || 1000} value={senderAccountLimit} onChange={(event) => setSenderAccountLimit(Math.min(overview?.outlookPool.total || 1000, Math.max(1, Number(event.target.value) || 1)))} /></label>
                                <label className="bulk-field"><span>每个账号每日上限（UTC）</span><input className="input" type="number" min={1} max={overview?.worker.outlookHardMaxPerSenderDaily || outlookPerSenderDailyLimit} value={outlookPerSenderDailyLimit} disabled={!outlookDailyLimitAdjustable} onChange={(event) => {
                                    initializedOutlookDailyLimitRef.current = true;
                                    setOutlookPerSenderDailyLimit(Math.min(overview?.worker.outlookHardMaxPerSenderDaily || 100, Math.max(1, Number(event.target.value) || 1)));
                                }} /></label>
                                <p className="bulk-help">每日账号额度上限：{plannedOutlookAccounts.toLocaleString()} × {outlookPerSenderDailyLimit} = {plannedOutlookCapacity.toLocaleString()} 封。
                                    {outlookGlobalDelayMs > 0
                                        ? <>发送间隔为{secondsRange}，实际发送量还受全局间隔限制。</>
                                        : <>同一账号的发送间隔 {secondsRange}。</>}
                                </p>
                            </>}
                            {senderProvider === 'spacemail' && <p className="bulk-help">当前有 {overview?.totals.configuredSenders || 0} 个发件账号，今日剩余额度 {overview?.totals.remainingCapacity || 0} 封。</p>}
                            {selectedCampaign?.targetAudience?.senderEmail && <p className="bulk-help">本次跟进指定：{selectedCampaign.targetAudience.senderDisplayName} · {selectedCampaign.targetAudience.senderEmail}。已回复客户不再跟进。</p>}
                        </details>
                        {externalMode && (checkingPreflight ? <p className="bulk-help" role="status">正在检查联系人…</p> : preflight ? <PreflightSummary data={preflight} /> : <p className="bulk-help">准备发送时自动检查邮箱、邮件变量和额度。</p>)}
                        {preflightError && <p className="send-readiness-warning">暂时无法检查：{preflightError}</p>}
                        <button className="btn btn-primary btn-block btn-lg" disabled={previewMode || !selectedCampaignId || !!busy || !validBatchLimit || (externalMode && (!subject.trim() || !bodyTemplate.trim()))} onClick={() => externalMode ? prepareSend() : void createJob()}>
                            {busy === 'job' ? <RefreshCw size={16} className="spin" /> : externalMode ? <Send size={16} /> : <ShieldCheck size={16} />}
                            {previewMode ? '仅供预览' : busy === 'job' ? '正在测试…' : externalMode ? '发送邮件' : '运行模拟测试'}
                        </button>
                        <p className="bulk-help launch-next-step">{externalMode ? '确认人数后开始发送，硬退信自动单独记录并排除。' : '模拟测试不会联系任何收件人。'}</p>
                        {externalSwitchOn && !externalMode && <p className="launch-warning">服务器当前处于开发模式，暂不允许对外发送。</p>}
                    </section>
                </aside>
            </div>
            {sendConfirmation && <SendConfirmationDialog
                config={sendConfirmation} request={api}
                onClose={() => { setSendConfirmation(null); setPreflightRevision((value) => value + 1); void loadPersonalization(true); }}
                onReady={(result) => { setPreflight(result); setPreflightRevision((value) => value + 1); }}
                onSent={(jobId, reused) => {
                    liveIntentRef.current = null;
                    setSendConfirmation(null);
                    setNotice(reused ? '已打开现有任务，未重复创建。' : '发送任务已创建，可在发送记录中查看进度、暂停或取消。');
                    void loadDashboard(true); void loadAttempts(jobId);
                    setPreflightRevision((value) => value + 1);
                }}
            />}
        </div>
    );
}

function Quality({ label, value, tone }: { label: string; value: number; tone: string }) {
    return <div className={`quality-item ${tone}`}><span>{label}</span><strong>{value.toLocaleString()}</strong></div>;
}

function SummaryLine({ label, value }: { label: string; value: string }) {
    return <div><span>{label}</span><strong title={value}>{value}</strong></div>;
}
